/* ─── window.storage polyfill ───────────────────────────────────
   The imported prototype (src/aurio.jsx) was authored against an
   artifact-preview sandbox that exposes an async `window.storage`
   API: `window.storage.get(key) -> { value } | undefined` and
   `window.storage.set(key, stringValue) -> void`.

   This shim implements the same async contract in a real browser.
   Do NOT edit aurio.jsx to talk to localStorage directly — keep this
   shim as the only bridge so the prototype stays portable if it's
   ever re-run inside an artifact sandbox again.

   Durability (v0.7.0): localStorage alone proved lossy — browsers
   evict it under storage pressure and "clear browsing data" wipes it.
   Every write goes to BOTH localStorage and IndexedDB; reads prefer
   localStorage and fall back to IndexedDB, restoring the localStorage
   copy when only IndexedDB survived. We also request persistent
   storage so the browser exempts this origin from eviction.

   Rename (v0.16.0): the app was renamed Solvente → Aurio, including
   internal storage keys. `migrateLegacyStorage()` copies every legacy
   `solvente-*` key (localStorage + the old IndexedDB) forward to the
   new `aurio-*` names, WITHOUT deleting the originals — they stay as
   a rollback net for a few releases. main.jsx awaits this before the
   app mounts, so the first Aurio-keyed read already sees the data. */

const DB_NAME = "aurio-storage";
const LEGACY_DB_NAME = "solvente-storage";
const DB_STORE = "kv";

/* Legacy key → new key. Covers state, rolling backup, preserved-corrupt
   payloads, the reminder-dedup log, and the landing-page "entered" flag. */
const LEGACY_KEY_MAP = {
  "solvente-state-v2": "aurio-state-v1",
  "solvente-state-v2.backup": "aurio-state-v1.backup",
  "solvente-state-v2.corrupt": "aurio-state-v1.corrupt",
  "solvente-notified-v1": "aurio-notified-v1",
  "solvente-entered": "aurio-entered",
};

let dbPromise = null;

function openNamedDb(name) {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") { resolve(null); return; }
    try {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => { req.result.createObjectStore(DB_STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function openDb() {
  if (!dbPromise) dbPromise = openNamedDb(DB_NAME);
  return dbPromise;
}

function idbGetFrom(db, key) {
  if (!db) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    try {
      const rq = db.transaction(DB_STORE, "readonly").objectStore(DB_STORE).get(key);
      rq.onsuccess = () => resolve(typeof rq.result === "string" ? rq.result : undefined);
      rq.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

async function idbGet(key) {
  return idbGetFrom(await openDb(), key);
}

async function idbSet(key, value) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function idbDelete(key) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(DB_STORE, "readwrite");
      tx.objectStore(DB_STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/* One-time, copy-forward migration of legacy solvente-* data. Idempotent
   and non-destructive: a new key is only written when absent, and legacy
   copies are never deleted here. Safe to run on every startup. */
export async function migrateLegacyStorage() {
  if (typeof window === "undefined") return;

  // 1. localStorage: legacy → new (only where new is missing).
  for (const [oldKey, newKey] of Object.entries(LEGACY_KEY_MAP)) {
    try {
      if (window.localStorage.getItem(newKey) == null) {
        const v = window.localStorage.getItem(oldKey);
        if (v != null) window.localStorage.setItem(newKey, v);
      }
    } catch { /* storage blocked — nothing to migrate */ }
  }

  // 2. Legacy IndexedDB mirror → new DB (covers devices where only the
  //    IDB copy survived). Skip keys already present in the new DB.
  try {
    const legacyDb = await openNamedDb(LEGACY_DB_NAME);
    if (legacyDb) {
      for (const [oldKey, newKey] of Object.entries(LEGACY_KEY_MAP)) {
        const legacyVal = await idbGetFrom(legacyDb, oldKey);
        if (legacyVal === undefined) continue;
        const existing = await idbGet(newKey);
        if (existing === undefined) await idbSet(newKey, legacyVal);
        // Restore localStorage too if it's missing entirely.
        try {
          if (window.localStorage.getItem(newKey) == null) window.localStorage.setItem(newKey, legacyVal);
        } catch { /* best effort */ }
      }
      legacyDb.close();
    }
  } catch { /* legacy DB unreadable — localStorage path above still ran */ }
}

function installStorageShim() {
  if (typeof window === "undefined") return;
  if (window.storage && typeof window.storage.get === "function") return; // already present (e.g. sandbox)

  // Ask the browser to exempt this origin from storage eviction. Silent
  // best-effort: some browsers grant automatically for installed PWAs.
  try {
    navigator.storage?.persist?.().catch(() => {});
  } catch {
    /* unsupported */
  }

  window.storage = {
    async get(key) {
      let value = null;
      try {
        value = window.localStorage.getItem(key);
      } catch (err) {
        console.error("[storage shim] localStorage get failed", err);
      }
      if (value === null) {
        // localStorage copy is gone — recover from the IndexedDB mirror.
        const mirrored = await idbGet(key);
        if (mirrored === undefined) return undefined;
        try { window.localStorage.setItem(key, mirrored); } catch { /* keep serving from IDB */ }
        return { value: mirrored };
      }
      return { value };
    },
    async set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (err) {
        console.error("[storage shim] localStorage set failed", err);
      }
      await idbSet(key, value);
    },
    async delete(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
        console.error("[storage shim] localStorage delete failed", err);
      }
      await idbDelete(key);
    },
  };
}

export default installStorageShim;
